import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Bank,
  Blueprint,
  Broadcast,
  CalendarBlank,
  ChartLineUp,
  ChatCircleText,
  CheckCircle,
  ClockCounterClockwise,
  Compass,
  CreditCard,
  Database,
  DeviceMobile,
  Eye,
  FloppyDisk,
  Gauge,
  Handshake,
  IdentificationCard,
  ListBullets,
  MapTrifold,
  Megaphone,
  PlusCircle,
  Polygon,
  SidebarSimple,
  Stack,
  Tray,
  UserPlus,
  UsersThree,
} from "@phosphor-icons/react";
import {
  ASK_FOR_CARD_EMAIL_ACTION,
  ASK_FOR_CARD_EMAIL_CONTENT,
  ASK_FOR_CARD_EMAIL_SUBJECT,
  ASK_FOR_CARD_SMS_CONTENT,
} from "@ruta/shared";
import type { TourStep } from "@/components/ui/feature-tour";
import { APP_STORE_LINKS } from "@/components/navigation/CustomerLinkShare";
import { PAGES as TEMPLATE_PAGES } from "@/components/service-templates/details/pages";
import { useImportForkVisibility } from "@/hooks/useImportForkVisibility";
import {
  useCrews,
  useDashboardStats,
  useOrganization,
  useServiceTemplates,
  useTeamMembers,
} from "@/hooks/useQueries";
import { useRutaAiAccess } from "@/hooks/useRutaAiAccess";
import { useSetupChecklist } from "@/hooks/useSetupChecklist";

/**
 * Every stop the office app's walkthrough can make, one per page, built from what
 * the company already has. Two things read this list: the first-run tour, which
 * walks the header and then every page in sidebar order, and the help button in
 * the top bar, which pulls up just the stop for the page the person is on.
 */

/**
 * A tour step that opens a page before it speaks about it — or, for a stop about one
 * record (a customer, a service template), one that stays on the record's page.
 */
export interface FirstRunStep extends TourStep {
  /** The page this stop opens. Left out on a detail stop, which never navigates. */
  route?: string;
  /**
   * A detail stop's route pattern (`/customers/:id`): the help button offers it on any
   * page the pattern matches, and the stop leaves the page where it is.
   */
  match?: string;
  /** Paths the pattern also matches that are not this page (`/service-templates/merge`). */
  except?: string[];
  /** The service template editor page this stop opens, as the editor's own `#hash`. */
  hash?: string;
  /** Router state to arrive with — a prefilled draft, a panel to open. */
  state?: Record<string, unknown>;
  /** The stop's name in one or two words. */
  short: string;
}

function doneTitle(title: string, done: boolean) {
  return done ? `${title} — done` : title;
}

function count(n: number, word: string, plural = `${word}s`) {
  return `${n} ${n === 1 ? word : plural}`;
}

/** The crew app's store codes, in a row: scan one, the app installs. */
export function StoreCodes() {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 p-2.5 text-stone-900">
      <div className="flex items-center gap-1.5 text-xs text-stone-500">
        <DeviceMobile className="h-4 w-4 shrink-0 text-primary" />
        <span className="leading-snug">
          Crew app —<br />
          scan to install
        </span>
      </div>
      <div className="ml-auto flex items-center gap-3">
        {[
          { label: "iPhone", href: APP_STORE_LINKS.ios },
          { label: "Android", href: APP_STORE_LINKS.android },
        ].map(({ label, href }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="flex flex-col items-center gap-1 text-[11px] text-stone-600"
          >
            <QRCodeSVG
              value={href}
              size={56}
              bgColor="transparent"
              fgColor="currentColor"
            />
            <span className="font-medium">{label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * The stops for wherever the person is: the exact page (a Settings tab is its own
 * page), or failing that every stop on this path (Settings on any other tab offers
 * both of its stops). On one customer or one service template, the detail stops
 * whose pattern matches the path.
 */
export function stopsForLocation(
  pages: FirstRunStep[],
  pathname: string,
  search: string,
  details: FirstRunStep[] = [],
): FirstRunStep[] {
  const here = pathname + search;
  const exact = pages.filter((s) => s.route === here);
  if (exact.length > 0) return exact;
  const onPath = pages.filter((s) => s.route?.split("?")[0] === pathname);
  if (onPath.length > 0) return onPath;
  return details.filter(
    (s) =>
      !!s.match &&
      !!matchPath({ path: s.match, end: true }, pathname) &&
      !s.except?.includes(pathname),
  );
}

/**
 * Open a service template editor page by its hash. The editor follows `hashchange`;
 * a router navigate fires none, and setting the hash it already has fires none
 * either (the reader may have clicked to another page since), so the event is sent
 * by hand. replaceState keeps the tour's page flips out of the Back history, and
 * keeps the router's own state on the entry.
 */
function openEditorPage(hash: string) {
  if (window.location.hash !== hash) {
    window.history.replaceState(window.history.state, "", hash);
  }
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

/**
 * Open the page a step talks about as it comes up, so the reader sees the real thing
 * behind the words. Steps that open a Settings tab carry the tab in the query, so the
 * whole location is compared.
 */
export function useStepNavigation(steps: FirstRunStep[]) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const locationRef = useRef(pathname + search);
  locationRef.current = pathname + search;
  return useCallback(
    (id: string | null) => {
      if (!id) return;
      const step = steps.find((s) => s.id === id);
      if (!step) return;
      // A detail stop is about the record already open: never leave it.
      if (!step.route) {
        if (step.hash) openEditorPage(step.hash);
        return;
      }
      if (locationRef.current !== step.route) {
        navigate(step.route, step.state ? { state: step.state } : undefined);
      }
    },
    [steps, navigate],
  );
}

export interface TourStops {
  /** The work queue and the top bar — on every page, so they come first. */
  header: FirstRunStep[];
  /** One stop per page, in sidebar order, for the pages this person's sidebar has. */
  pages: FirstRunStep[];
  /**
   * Stops for one record's page — a customer, a service template. Only the help
   * button offers these; the first-run lap never opens a record.
   */
  details: FirstRunStep[];
}

const DETAIL_HINT = "Open one and press ? for a tour of that page.";

const CUSTOMER_PAGE = "/customers/:id";
const TEMPLATE_PAGE = "/service-templates/:templateId";

/**
 * The customer page (map-first workspace): the header, the map, the work column and
 * the right rail. Billing moved into the rail, so its stop says so. A company not
 * taking cards hears nothing about cards or their warnings.
 */
export function customerTourStops({
  showPayments,
}: {
  showPayments: boolean;
}): FirstRunStep[] {
  return [
    {
      id: "customer-header",
      match: CUSTOMER_PAGE,
      target: '[data-tour="customer-header"]',
      short: "Customer",
      title: "One page per customer",
      content:
        "Everything about this customer on one page: the map in the middle, their work on the left, the conversation on the right.",
      placement: "bottom",
      icon: <IdentificationCard className="h-5 w-5" />,
      callouts: [
        {
          target: '[data-tour="customer-identity"]',
          label: "Name and status. Click the name to rename them.",
        },
        {
          target: '[data-tour="customer-view-as"]',
          label: "View as customer opens their portal as they see it.",
        },
        {
          target: '[data-tour="customer-menu"]',
          label: "Customer menu: rename, new owner, contacts, deactivate.",
        },
      ],
    },
    {
      id: "customer-map",
      match: CUSTOMER_PAGE,
      target: '[data-tour="customer-map"]',
      short: "Map",
      title: "The map",
      content:
        "Their property, with other customers around it as grey dots. Hover a dot for the name and address; click it to open that customer.",
      placement: "left",
      icon: <MapTrifold className="h-5 w-5" />,
      callouts: [
        {
          target: '[data-tour="customer-map-bar"]',
          label:
            "What the property is measured at. Measure and price opens a sheet to draw areas on this map and price services from them.",
        },
      ],
    },
    {
      id: "customer-work",
      match: CUSTOMER_PAGE,
      target: ['[data-tour="customer-work"]', '[data-tour="customer-column"]'],
      short: "Work",
      title: "Who they are and their work",
      content:
        "The column on the left is this customer. It opens on Details; open any service or visit and its sheet slides in here while the map stays put.",
      placement: "right",
      icon: <PlusCircle className="h-5 w-5" />,
      callouts: [
        {
          target: '[data-tour="customer-work-tabs"]',
          label: "Details | Services | Visits: address and contacts, what they have, and every visit past and upcoming.",
        },
        {
          target: ['[data-tour="customer-add-service"]', '[data-tour="customer-work-services"]'],
          label: "Services → Add: one of your services, priced for them, or a custom one-off.",
        },
        {
          target: '[data-tour="customer-note"]',
          label: "Customer note: gate codes, how they like to be reached. Only your office sees it.",
        },
      ],
    },
    {
      id: "customer-rail",
      match: CUSTOMER_PAGE,
      target: '[data-tour="customer-rail"]',
      short: "Rail",
      title: "Messages, timeline, billing",
      content:
        "The panel on the right holds three tabs: Messages, Timeline and Billing. Billing lives here, not on a page of its own. The arrow at the end folds the panel away.",
      placement: "left",
      icon: <SidebarSimple className="h-5 w-5" />,
      callouts: [
        {
          target: '[data-tour="customer-rail-messages"]',
          label: "Messages: their texts and portal messages. A red number is unread.",
        },
        {
          target: '[data-tour="customer-rail-timeline"]',
          label: "Timeline: everything that has happened on the account, newest first.",
        },
        {
          target: '[data-tour="customer-rail-billing"]',
          label: showPayments
            ? "Billing: balance, card on file and payments. A warning mark on the tab means the card is missing, expired or about to, or the last charge failed."
            : "Billing: the balance, in red on the tab when they owe, and the account's activity.",
        },
      ],
    },
  ];
}

/**
 * The service template editor: a stop per editor page, worded from the editor's own
 * page list so the two can never say different things, then Publish and History.
 * Each page stop opens its page through the editor's hash.
 */
export function templateTourStops(): FirstRunStep[] {
  const pageStops = TEMPLATE_PAGES.map<FirstRunStep>((p, i) => ({
    id: `template-${p.id}`,
    match: TEMPLATE_PAGE,
    except: ["/service-templates/merge"],
    hash: `#${p.id}`,
    target: ['[data-tour="template-page"]', '[data-tour="template-hero"]'],
    short: p.title,
    title: p.title,
    content: p.intro,
    placement: "left",
    icon: <Blueprint className="h-5 w-5" />,
    callouts: [
      {
        target: `[data-tour="template-nav-${p.id}"]`,
        label:
          i === 0
            ? "The four pages, in order. The line under each says what's set inside; an orange dot means unpublished changes."
            : `Page ${i + 1} of ${TEMPLATE_PAGES.length}. Back and Next at the foot of each page walk them in order.`,
      },
    ],
  }));
  return [
    ...pageStops,
    {
      id: "template-publish",
      match: TEMPLATE_PAGE,
      except: ["/service-templates/merge"],
      target: ['[data-tour="template-publish"]', '[data-tour="template-hero"]'],
      short: "Publish",
      title: "Nothing saves until Publish",
      content:
        "Changes on every page wait as a draft, counted beside the name. Review & publish saves them all at once.",
      action:
        "When a change moves prices, Publish asks whether existing customers are repriced or keep what they pay.",
      placement: "bottom",
      icon: <FloppyDisk className="h-5 w-5" />,
      callouts: [
        {
          target: '[data-tour="template-rail"]',
          label:
            "Customers on this service, and the tile they see in the portal. A staged price change shows here how many prices would move.",
        },
      ],
    },
    {
      id: "template-history",
      match: TEMPLATE_PAGE,
      except: ["/service-templates/merge"],
      target: ['[data-tour="template-history-tab"]', '[data-tour="template-hero"]'],
      short: "History",
      title: "History",
      content: "Every published change to this service: what changed, who made it, and when.",
      action: "Open the History tab beside Configuration.",
      placement: "bottom",
      icon: <ClockCounterClockwise className="h-5 w-5" />,
    },
  ];
}

/**
 * Builds the stops from what the company already has, so a stop the setup checklist
 * has ticked says what it found rather than what to do — "your import brought in 42
 * customers", not "drop the file in here". The counts are fetched only while `enabled`.
 */
export function useTourStops({ enabled }: { enabled: boolean }): TourStops {
  const { checklist } = useSetupChecklist();
  const items = checklist?.items;
  // A company not taking cards never sees Billing (RUT-3503), so no step points there.
  const { showPayments } = useImportForkVisibility();
  // The Ruta AI sparkle is only in the top bar for plans that have it.
  const canUseRutaAi = useRutaAiAccess();
  const { pathname } = useLocation();

  const { data: templates } = useServiceTemplates({ enabled });
  const { data: team } = useTeamMembers({ enabled });
  const { data: crewsData } = useCrews({ enabled });
  const { data: stats } = useDashboardStats({ enabled });
  const { data: orgData } = useOrganization({ enabled });
  const counts = useMemo(
    () => ({
      // A template that remembers its original name came over in an import.
      templates: templates?.length,
      importedTemplates: templates?.filter((t) => !!t.originalName).length,
      people: team?.members.length,
      crews: crewsData?.crews.length,
      customers: stats?.customerPaymentCoverage.totalActiveCustomers,
      imported: !!orgData?.organization?.firstRun?.importId,
    }),
    [templates, team, crewsData, stats, orgData],
  );

  // The sidebar only lists what this person can open, so the walk covers the pages
  // whose links are actually there — no Team for a "just me" company, no Subcontractors
  // for one that does all the work itself. Re-read on every page, and whenever a
  // caller turns `enabled` on (the help menu opening), since the sidebar can change
  // with the org and can still be filling in when the top bar first mounts.
  const [nav, setNav] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    const links = new Set(
      Array.from(document.querySelectorAll('[data-tour^="nav-"]')).map(
        (el) => el.getAttribute("data-tour") ?? "",
      ),
    );
    setNav((prev) =>
      prev.size === links.size && Array.from(links).every((l) => prev.has(l))
        ? prev
        : links,
    );
  }, [pathname, enabled]);

  return useMemo<TourStops>(() => {
    const teamDone = !!items?.inviteCrewMembers;
    const crewsDone = !!items?.organizeCrews;
    const customersDone = !!items?.importCustomers;
    const servicesDone = !!items?.reviewServices;
    const paymentsDone = !!items?.activatePayments;
    const has = (link: string) => nav.has(`nav-${link}`);

    const header: FirstRunStep[] = [
      {
        id: "queue",
        route: "/assistant",
        target: '[data-tour="work-queue"]',
        short: "Work queue",
        title: "Your work queue",
        content:
          "Everything waiting on the office, in the header on every page. A number shows up when something needs you.",
        action:
          "Click a stage to open its list. Preferences → Work queue picks what it tracks.",
        placement: "bottom",
        icon: <Tray className="h-5 w-5" />,
        // The stages sit a few pixels apart, too close for a chip each — the card
        // walks them in order, and each one lights up on the page as its line is hovered.
        callouts: [
          {
            target: '[data-tour="work-queue-messages"]',
            pin: false,
            label: "Unread messages — customer conversations you haven't read.",
          },
          {
            target: '[data-tour="work-queue-rates"]',
            pin: false,
            label: "Rate requests — customers asking what a service will cost.",
          },
          {
            target: '[data-tour="work-queue-activations"]',
            pin: false,
            label: "Pending activations — services accepted, waiting to start.",
          },
          {
            target: '[data-tour="work-queue-review"]',
            pin: false,
            label: "Awaiting review — visits that need an office decision.",
          },
          {
            target: '[data-tour="work-queue-photos"]',
            pin: false,
            label: "Photos to review — crew photos before they reach the customer.",
          },
          // The payments stage is only in the queue for a company taking cards.
          ...(showPayments
            ? [
                {
                  target: '[data-tour="work-queue-payments"]',
                  pin: false,
                  label: "Payment issues — missing, expired or failing cards.",
                },
              ]
            : []),
        ],
      },
      {
        id: "topbar",
        route: "/assistant",
        target: '[data-tour="header-tools"]',
        short: "Top bar",
        title: "The rest of the top bar",
        content: "These follow you onto every page.",
        placement: "bottom",
        icon: <Compass className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="header-search"]',
            label: "Search — any customer, address or visit. ⌘K from anywhere.",
          },
          {
            target: '[data-tour="header-weather"]',
            label: "Weather for your service area.",
          },
          {
            target: '[data-tour="header-links"]',
            label: "Share — your customer portal link and the crew app.",
          },
          ...(canUseRutaAi
            ? [
                {
                  target: '[data-tour="header-ai"]',
                  label: "Ruta AI — ask anything; it can take you straight there.",
                },
              ]
            : []),
          {
            target: '[data-tour="header-help"]',
            label: "Help — a tour of the page you're on, or the full tour again.",
          },
          {
            target: '[data-tour="header-bell"]',
            label: "Notifications — what needs a look, newest first.",
          },
          {
            target: '[data-tour="header-profile"]',
            label: "You — profile, preferences, sign out.",
          },
        ],
      },
    ];

    // The pages, in sidebar order.
    const pages: FirstRunStep[] = [
      // ── Operate ──
      {
        id: "live",
        route: "/assistant",
        target: ['[data-tour="live-kpis"]', '[data-tour="nav-assistant"]'],
        short: "Live",
        title: "Live",
        content:
          "The day at a glance once crews are out. It fills in as soon as work starts.",
        placement: "bottom",
        icon: <Broadcast className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="live-kpis"]',
            label: "Today's numbers — visits done, revenue collected, who's clocked in.",
          },
          {
            target: '[data-tour="live-crews"]',
            label: "Each crew and the visit it is on right now.",
          },
          {
            target: '[data-tour="live-map"]',
            label: "Every job on the map as it happens.",
          },
          {
            target: '[data-tour="live-feed"]',
            label: "Activity — what just happened, and what's left to set up.",
          },
        ],
      },
    ];

    if (has("map")) {
      pages.push({
        id: "map",
        route: "/map",
        target: ['[data-tour="map-toolbar"]', '[data-tour="nav-map"]'],
        short: "Map",
        title: "Map",
        content:
          "Every customer and every crew on one map. The bar at the bottom is the whole map — each switch is a layer.",
        action:
          "Flip a layer on or off. The sliders beside it open that layer's filters. Click any pin to open it.",
        placement: "top",
        icon: <MapTrifold className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="map-layer-customers"]',
            label: "Customers — every customer pinned, coloured by status. Cluster groups nearby pins; the sliders filter by service, status or name.",
          },
          {
            target: '[data-tour="map-layer-parcels"]',
            label: "Parcels — county property lines, for measuring a lot and seeing where one ends.",
          },
          {
            target: '[data-tour="map-layer-visits"]',
            label: "Visits — the day's stops. Pick a date and a crew; Trail draws where the truck actually went.",
          },
          {
            target: '[data-tour="map-layer-members"]',
            label: "Members — who's clocked in and where they are right now.",
          },
          {
            target: '[data-tour="map-layer-routes"]',
            label: "Routes — the driving order for one crew's day.",
          },
          {
            target: '[data-tour="map-legend"]',
            label: "The crew list — online in green, tap one to follow them.",
          },
        ],
      });
    }

    if (has("schedule")) {
      pages.push({
        id: "schedule",
        route: "/schedule",
        target: ['[data-tour="schedule-today"]', '[data-tour="nav-schedule"]'],
        short: "Schedule",
        title: "Schedule",
        content:
          "Today's visits, in the order each crew will drive them. Whatever is here in the morning is what crews see on their phones.",
        action:
          "Pick a crew to see its route, then drag a visit to change the order. Click a visit for its details.",
        placement: "right",
        icon: <CalendarBlank className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="schedule-crews"]',
            label: "Crews — pick one to see just its route and who is on it today.",
          },
          {
            target: '[data-tour="schedule-glance"]',
            label: "Today at a glance — done, in progress, not started. Click one to filter the list.",
          },
          {
            target: '[data-tour="schedule-week"]',
            label: "This week — the visits coming up after today.",
          },
          {
            target: '[data-tour="visits-filters"]',
            label: "Search today's list, or narrow it by status or service.",
          },
        ],
      });
    }

    if (has("review")) {
      pages.push({
        id: "review",
        route: "/review",
        target: ['[data-tour="review-queue"]', '[data-tour="nav-review"]'],
        short: "Review",
        title: "Review",
        content:
          "Visits that need an office decision before they bill, and crew photos before customers see them.",
        action:
          "Pick a visit, check it against the map and the crew's notes, then Approve — or adjust the amount first. Nothing bills until it clears here.",
        placement: "top",
        icon: <Eye className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="tab-visits"]',
            label: "Visits — time, price and notes to check before the charge goes out.",
          },
          {
            target: '[data-tour="tab-photos"]',
            label: "Photos — pick the ones worth sending; the rest stay on the record.",
          },
          {
            target: '[data-tour="visits-filters"]',
            label: "Search the queue, and switch between one at a time (Queue) and all at once (Table).",
          },
        ],
      });
    }

    // ── Grow ──
    if (has("messages")) {
      pages.push({
        id: "messages",
        route: "/messages",
        target: ['[data-tour="messages-content"]', '[data-tour="nav-messages"]'],
        short: "Messaging",
        title: "Messaging",
        content:
          "Customer texts and team chat, one place. The routine texts Ruta sends for you.",
        placement: "top",
        icon: <ChatCircleText className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="tab-customers"]',
            label: "Customers — their texts and portal messages, in one inbox.",
          },
          {
            target: '[data-tour="tab-team-chat"]',
            label: "Team — chat with your crews, per crew or per visit.",
          },
          {
            target: '[data-tour="messages-compose"]',
            label: "New message — start a conversation with any customer.",
          },
        ],
      });
    }

    if (has("campaigns")) {
      pages.push(
        showPayments
          ? // Taking cards only pays off once customers have one on file. The ask is
            // already written; this stop opens it prefilled and shows how to make it repeat.
            {
              id: "campaigns",
              route: "/campaigns",
              state: {
                openHistory: true,
                draft: {
                  emailSubject: ASK_FOR_CARD_EMAIL_SUBJECT,
                  emailContent: ASK_FOR_CARD_EMAIL_CONTENT,
                  smsContent: ASK_FOR_CARD_SMS_CONTENT,
                  filters: { paymentStatus: "missing" },
                  emailAction: {
                    label: ASK_FOR_CARD_EMAIL_ACTION.label,
                    url: ASK_FOR_CARD_EMAIL_ACTION.url,
                  },
                },
              },
              target: ['[data-tour="campaign-email"]', '[data-tour="nav-campaigns"]'],
              short: "Campaigns",
              title: "Campaigns — get cards on file",
              content:
                "One message to many customers. A customer adds a card once; every visit after that charges it — and the ask is written for you. This is it.",
              action:
                "Send it now, or use the arrow beside Send to repeat it weekly until everyone has a card.",
              placement: "left",
              icon: <CreditCard className="h-5 w-5" />,
              callouts: [
                {
                  target: '[data-tour="campaign-audience"]',
                  label: "Goes to everyone with a live service and no card on file.",
                },
                {
                  target: '[data-tour="campaign-email"]',
                  label: "The built-in ask — the button says Add your card.",
                },
                {
                  target: '[data-tour="campaign-send-options"]',
                  label: "The arrow beside Send sets it to repeat weekly.",
                },
                {
                  target: '[data-tour="campaign-repeating"]',
                  label: "Repeating campaigns live here — pause or stop any time.",
                },
              ],
            }
          : {
              id: "campaigns",
              route: "/campaigns",
              state: { openHistory: true },
              target: ['[data-tour="campaign-composer"]', '[data-tour="nav-campaigns"]'],
              short: "Campaigns",
              title: "Campaigns",
              content:
                "One message to many customers — by service, city or status — as a text, an email, or both.",
              action:
                "Pick the audience, write it once, press Send. The arrow beside Send makes it repeat.",
              placement: "left",
              icon: <Megaphone className="h-5 w-5" />,
              callouts: [
                {
                  target: '[data-tour="campaign-audience"]',
                  label: "Who gets it — filtered by service, city or status.",
                },
                {
                  target: '[data-tour="campaign-send-options"]',
                  label: "The arrow beside Send sets it to repeat.",
                },
                {
                  target: '[data-tour="campaign-repeating"]',
                  label: "Repeating campaigns live here — pause or stop any time.",
                },
              ],
            },
      );
    }

    // ── Data ──
    if (has("customers")) {
      pages.push({
        id: "customers",
        route: "/customers",
        target: ['[data-tour="customers-list"]', '[data-tour="nav-customers"]'],
        short: "Customers",
        title: doneTitle("Customers", customersDone),
        content:
          customersDone && counts.customers !== undefined
            ? `${count(counts.customers, "customer")} in Ruta. Services, visits, balance and messages sit on one record. ${DETAIL_HINT}`
            : `Everyone you work for. Services, visits, balance and messages sit on one record. ${DETAIL_HINT}`,
        action: customersDone
          ? "Open any of them to check what came over."
          : "Add Customer adds one at a time; imports land here too.",
        placement: "top",
        icon: customersDone ? (
          <CheckCircle className="h-5 w-5" />
        ) : (
          <UsersThree className="h-5 w-5" />
        ),
        callouts: [
          {
            target: '[data-tour="customers-filters"]',
            label: "Search, then filter by service, status, city, payment or balance.",
          },
          {
            target: '[data-tour="customers-add"]',
            label: "Add Customer — one at a time. Imports land here too.",
          },
        ],
      });
    }

    if (has("services")) {
      pages.push({
        id: "subscriptions",
        route: "/services",
        target: ['[data-tour="subscriptions-list"]', '[data-tour="nav-services"]'],
        short: "Services",
        title: "Services",
        content:
          "Every service a customer has — who has what, at what price, how often. This is what the schedule is built from.",
        action:
          "Filter by service or status. Open one to change its price, its cadence, or pause it.",
        placement: "top",
        icon: <Stack className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="tab-services"]',
            label: "All Services — every customer's service, one row each.",
          },
          {
            target: '[data-tour="tab-config"]',
            label: "Configuration — rate requests and accepted services waiting to be set up before they start.",
          },
          {
            target: '[data-tour="tab-pricing"]',
            label: "Pricing — tune rates across many services at once.",
          },
        ],
      });
    }

    if (has("visits")) {
      pages.push({
        id: "visits",
        route: "/visits",
        target: ['[data-tour="visits-table"]', '[data-tour="nav-visits"]'],
        short: "Visits",
        title: "Visits",
        content:
          "Every visit, past and upcoming, in one list — with its crew, its time on site and what it billed.",
        action:
          "Filter by status or date. Open one for the photos, the notes and the crew's time.",
        placement: "top",
        icon: <ListBullets className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="visits-filters"]',
            label: "Search, then filter by date, status, crew, service or payment.",
          },
        ],
      });
    }

    // ── Insights ──
    if (has("revenue")) {
      pages.push({
        id: "revenue",
        route: "/revenue",
        target: [
          '[data-tour="revenue-cards"]',
          '[data-tour="report-period"]',
          '[data-tour="nav-revenue"]',
        ],
        short: "Revenue",
        title: "Revenue",
        content:
          "Money in, sales tax collected, and what's still outstanding, for whatever period you pick.",
        action: "Click a card for the visits and payments behind it.",
        placement: "bottom",
        icon: <ChartLineUp className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="report-period"]',
            label: "The period — every number on the page follows it.",
          },
          {
            target: '[data-tour="revenue-tax"]',
            label: "Sales tax, once you file through Ruta — what's due each period, the export for the state, and Mark paid.",
          },
          {
            target: '[data-tour="revenue-chart"]',
            label: "Revenue over the period, by service.",
          },
        ],
      });
    }

    if (has("revenue-performance")) {
      pages.push({
        id: "performance",
        route: "/revenue/performance",
        target: [
          '[data-tour="performance-daily"]',
          '[data-tour="nav-revenue-performance"]',
        ],
        short: "Performance",
        title: "Performance",
        content:
          "Day by day, the hours visits were budgeted for against the hours crews actually worked, with the jobs done each day.",
        action:
          "Days where actual runs well over budget point at a service that's underpriced or a crew that's stretched.",
        placement: "bottom",
        icon: <Gauge className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="report-period"]',
            label: "The period to chart.",
          },
          {
            target: '[data-tour="performance-retention"]',
            label: "Retention — how many customers you keep, month to month.",
          },
        ],
      });
    }

    if (has("revenue-subcontractors")) {
      pages.push({
        id: "subcontractors",
        route: "/revenue/subcontractors",
        target: [
          '[data-tour="subcontractors-breakdown"]',
          '[data-tour="report-period"]',
          '[data-tour="nav-revenue-subcontractors"]',
        ],
        short: "Subcontractors",
        title: "Subcontractors",
        content:
          "The work you send out and what you owe for it, by company and by period, beside what your own crews brought in.",
        action: "Export a company's visits for the period, or mark its work paid.",
        placement: "bottom",
        icon: <Handshake className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="report-period"]',
            label: "The period — pick the pay period you're settling.",
          },
        ],
      });
    }

    // ── Configure ──
    if (has("service-templates")) {
      // Services sit ahead of everything that picks from them — a quote is priced
      // from one and the schedule lays visits out from one.
      pages.push({
        id: "services",
        route: "/service-templates",
        target: ['[data-tour="services-list"]', '[data-tour="nav-service-templates"]'],
        short: "Templates",
        title: doneTitle("Service Templates", servicesDone),
        content: `${
          servicesDone
            ? counts.templates !== undefined
              ? counts.importedTemplates
                ? `${count(counts.importedTemplates, "service")} came over in your import${counts.templates > counts.importedTemplates ? `, ${count(counts.templates, "service")} in all` : ""}, each with a price.`
                : `${count(counts.templates, "service")} set up, each with a price.`
              : "The services you sell and what each one costs. Yours are in."
            : "The services you sell and what each one costs. Quotes and the schedule pick from these."
        } ${DETAIL_HINT}`,
        action: servicesDone
          ? "Open one to check its price and what it includes."
          : "Add the ones you sell — mowing, cleanups, snow — with a price.",
        placement: "right",
        icon: servicesDone ? (
          <CheckCircle className="h-5 w-5" />
        ) : (
          <Blueprint className="h-5 w-5" />
        ),
        callouts: [
          {
            target: '[data-tour="services-kpis"]',
            label: "How many services are live, who subscribes, and what they bring in a year.",
          },
          {
            target: '[data-tour="services-filters"]',
            label: "Filter by status or category, or search by name.",
          },
          {
            target: '[data-tour="services-list"]',
            label: "The services themselves — open one to change its price or what it includes.",
          },
        ],
      });
    }

    if (has("service-area")) {
      pages.push({
        id: "service-area",
        route: "/service-area",
        target: ['[data-tour="service-area-list"]', '[data-tour="nav-service-area"]'],
        short: "Service area",
        title: "Service Area",
        content:
          "The areas you work, drawn on the map or added by ZIP. The customer portal only takes requests from inside them.",
        action: "Draw Area traces one by hand; Add by ZIP brings in a whole ZIP code.",
        placement: "right",
        icon: <Polygon className="h-5 w-5" />,
        callouts: [
          {
            target: '[data-tour="service-area-list"]',
            label: "Your areas and the crews that cover each. Click one to find it on the map.",
          },
          {
            target: '[data-tour="service-area-actions"]',
            label: "Draw Area or Add by ZIP.",
          },
        ],
      });
    }

    if (has("team")) {
      pages.push({
        id: "team",
        route: "/team",
        target: ['[data-tour="team-invite"]', '[data-tour="nav-team"]'],
        short: "Team",
        title: doneTitle("Team", teamDone),
        content: teamDone
          ? crewsDone
            ? counts.people !== undefined && counts.crews !== undefined
              ? `${count(counts.people, "person", "people")} on the team, in ${count(counts.crews, "crew")} — the trucks that go out together.`
              : "Your people are in and grouped into crews — the trucks that go out together."
            : counts.people !== undefined
              ? `${count(counts.people, "person", "people")} on the team. Crews are the trucks that go out together.`
              : "Your people are in. Crews are the trucks that go out together."
          : "Your people, grouped into crews — the trucks that go out together.",
        action: teamDone
          ? crewsDone
            ? undefined
            : "Group them into crews on the Crews tab."
          : "Press Invite Member — each person gets a text with the crew app and a sign-in code.",
        placement: "bottom",
        icon: teamDone ? (
          <CheckCircle className="h-5 w-5" />
        ) : (
          <UserPlus className="h-5 w-5" />
        ),
        callouts: [
          {
            target: '[data-tour="tab-members"]',
            label: "Members — everyone on the team and their role.",
          },
          {
            target: '[data-tour="tab-crews"]',
            label: "Crews — who rides together. Visits are scheduled to a crew.",
          },
          {
            target: '[data-tour="tab-subcontractors"]',
            label: "Subcontractors — outside companies you send work to.",
          },
        ],
        // The crew app is how the people invited here work; its install codes ride
        // along on this stop rather than getting a stop of their own.
        render: <StoreCodes />,
      });
    }

    if (has("settings")) {
      pages.push({
        id: "import",
        route: "/settings?tab=data",
        target: ['[data-tour="settings-import"]', '[data-tour="nav-settings"]'],
        short: "Import",
        title: doneTitle("Settings — Data & Import", customersDone),
        content: customersDone
          ? counts.customers !== undefined
            ? `Your import brought in ${count(counts.customers, "customer")}${counts.importedTemplates ? ` and ${count(counts.importedTemplates, "service")}` : ""}.`
            : "Your customers are in."
          : "Bring customers over from your old system — a spreadsheet, a PDF, even a photo of a list.",
        action: customersDone
          ? "This is where you come back with the next export."
          : "Drop the file in here. Nothing is saved until you approve what it found.",
        placement: "left",
        icon: customersDone ? (
          <CheckCircle className="h-5 w-5" />
        ) : (
          <Database className="h-5 w-5" />
        ),
      });

      if (showPayments) {
        pages.push({
          id: "payments",
          route: "/settings?tab=billing",
          target: ['[data-tour="settings-payments"]', '[data-tour="nav-settings"]'],
          short: "Get paid",
          title: doneTitle("Settings — Billing & Payments", paymentsDone),
          content: paymentsDone
            ? "Your merchant account is approved. Card and bank payments land in your bank."
            : "Customers pay by card or bank, straight to your account.",
          action: paymentsDone
            ? undefined
            : "Finish the merchant application — EIN, bank account, a few details. Approval takes about two business days.",
          placement: "left",
          icon: paymentsDone ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <Bank className="h-5 w-5" />
          ),
        });
      }
    }

    const details = [
      ...customerTourStops({ showPayments }),
      ...templateTourStops(),
    ];

    return { header, pages, details };
  }, [items, nav, showPayments, canUseRutaAi, counts]);
}
